import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import {
  BarChart3, TrendingUp, TrendingDown, Truck, Users, Fuel, Wrench,
  DollarSign, AlertTriangle, Clock, Target, RefreshCw, Calendar,
  PieChart, Activity, ChevronDown, Download, Filter } from
'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { useFleetAPI } from '../hooks/useFleetData';

const API_URL = API_BASE;

const FleetAnalyticsPage = () => {const { t } = useTranslation("fleet");
  const [period, setPeriod] = useState('month');
  const [dashboard, setDashboard] = useState(null);
  const [vehiclePerformance, setVehiclePerformance] = useState([]);
  const [driverPerformance, setDriverPerformance] = useState([]);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  const { fetchAPI } = useFleetAPI();

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [dashboardData, vehicleData, driverData, costData] = await Promise.all([
      fetchAPI(`/analytics/dashboard?period=${period}`),
      fetchAPI(`/analytics/vehicle-performance?period=${period}`),
      fetchAPI(`/analytics/driver-performance?period=${period}`),
      fetchAPI(`/analytics/cost-breakdown?period=${period}`)]
      );

      setDashboard(dashboardData);
      setVehiclePerformance(vehicleData?.vehicles || []);
      setDriverPerformance(driverData?.drivers || []);
      setCostBreakdown(costData);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = 'blue' }) =>
  <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {trend &&
          <div className={`flex items-center mt-1 text-sm ${
          trend === 'up' ? 'text-green-600' : 'text-red-600'}`
          }>
                {trend === 'up' ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                {trendValue}
              </div>
          }
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-${color}-100`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>;


  const ProgressBar = ({ value, max, color = 'blue' }) =>
  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
      <div
      className={`h-full bg-${color}-500 rounded-full transition-all duration-500`}
      style={{ width: `${Math.min(value / max * 100, 100)}%` }} />
    
    </div>;


  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
      </div>);

  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("fleetAnalytics", "Fleet Analytics")}</h1>
          <p className="text-gray-500">{t("performanceInsightsAnd", "Performance insights and cost analysis")}</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="px-4 py-2 border rounded-lg text-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}>
            
            <option value="day">{t("today", "Today")}</option>
            <option value="week">{t("thisWeek", "This Week")}</option>
            <option value="month">{t("thisMonth", "This Month")}</option>
            <option value="year">{t("thisYear", "This Year")}</option>
          </select>
          <Button variant="outline" onClick={loadAnalytics}>
            <RefreshCw className="w-4 h-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />{t("export", "Export")}

          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {dashboard &&
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">{t("utilizationRate", "Utilization Rate")}</p>
                  <p className="text-3xl font-bold text-blue-700">{dashboard.fleet_overview?.utilization_rate || 0}%</p>
                </div>
                <Target className="w-10 h-10 text-blue-500" />
              </div>
              <ProgressBar value={dashboard.fleet_overview?.utilization_rate || 0} max={100} color="blue" />
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">{t("ontimeRate", "On-Time Rate")}</p>
                  <p className="text-3xl font-bold text-green-700">{dashboard.trip_metrics?.on_time_rate || 0}%</p>
                </div>
                <Clock className="w-10 h-10 text-green-500" />
              </div>
              <ProgressBar value={dashboard.trip_metrics?.on_time_rate || 0} max={100} color="green" />
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">{t("fuelEfficiency", "Fuel Efficiency")}</p>
                  <p className="text-3xl font-bold text-purple-700">{dashboard.fuel_metrics?.fuel_efficiency_km_l || 0}</p>
                  <p className="text-xs text-purple-500">{t("kml", "km/L")}</p>
                </div>
                <Fuel className="w-10 h-10 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">{t("costPerKm", "Cost per KM")}</p>
                  <p className="text-3xl font-bold text-amber-700">{t("r", "R$")}{dashboard.financial_metrics?.cost_per_km || 0}</p>
                </div>
                <DollarSign className="w-10 h-10 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      }

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">{t("overview", "Overview")}</TabsTrigger>
          <TabsTrigger value="vehicles">{t("vehiclePerformance", "Vehicle Performance")}</TabsTrigger>
          <TabsTrigger value="drivers">{t("driverPerformance", "Driver Performance")}</TabsTrigger>
          <TabsTrigger value="costs">{t("costAnalysis", "Cost Analysis")}</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          {dashboard &&
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Fleet Overview */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-blue-500" />{t("fleetOverview", "Fleet Overview")}

                </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t("totalVehicles", "Total Vehicles")}</span>
                      <span className="font-bold">{dashboard.fleet_overview?.total_vehicles || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t("activeVehicles", "Active Vehicles")}</span>
                      <span className="font-bold text-green-600">{dashboard.fleet_overview?.active_vehicles || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t("inMaintenance", "In Maintenance")}</span>
                      <span className="font-bold text-amber-600">{dashboard.fleet_overview?.vehicles_in_maintenance || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t("totalDistance", "Total Distance")}</span>
                      <span className="font-bold">{(dashboard.trip_metrics?.total_distance_km || 0).toLocaleString()}{t("km", "km")}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Trip Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-green-500" />{t("tripMetrics", "Trip Metrics")}

                </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t("totalTrips", "Total Trips")}</span>
                      <span className="font-bold">{dashboard.trip_metrics?.total_trips || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t("completed", "Completed")}</span>
                      <span className="font-bold text-green-600">{dashboard.trip_metrics?.completed_trips || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t("ontimeRate", "On-Time Rate")}</span>
                      <span className="font-bold">{dashboard.trip_metrics?.on_time_rate || 0}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Maintenance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-amber-500" />{t("maintenance", "Maintenance")}

                </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t("maintenanceEvents", "Maintenance Events")}</span>
                      <span className="font-bold">{dashboard.maintenance_metrics?.maintenance_events || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t("maintenanceCost", "Maintenance Cost")}</span>
                      <span className="font-bold">{t("r", "R$")}{(dashboard.maintenance_metrics?.maintenance_cost || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Safety */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />{t("safetyMetrics", "Safety Metrics")}

                </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t("totalAlerts", "Total Alerts")}</span>
                      <span className="font-bold">{dashboard.safety_metrics?.total_alerts || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t("criticalAlerts", "Critical Alerts")}</span>
                      <span className="font-bold text-red-600">{dashboard.safety_metrics?.critical_alerts || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t("harshBrakingEvents", "Harsh Braking Events")}</span>
                      <span className="font-bold">{dashboard.safety_metrics?.harsh_braking_events || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t("speedingEvents", "Speeding Events")}</span>
                      <span className="font-bold">{dashboard.safety_metrics?.speeding_events || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          }
        </TabsContent>

        {/* Vehicle Performance Tab */}
        <TabsContent value="vehicles">
          <Card>
            <CardHeader>
              <CardTitle>{t("vehiclePerformanceRankings", "Vehicle Performance Rankings")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 font-medium text-gray-500">{t("vehicle", "Vehicle")}</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-500">{t("status", "Status")}</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">{t("trips", "Trips")}</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">{t("distance", "Distance")}</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">{t("fuelEff", "Fuel Eff.")}</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">{t("totalCost", "Total Cost")}</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">{t("costkm", "Cost/km")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehiclePerformance.map((v) =>
                    <tr key={v.vehicle_id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-2">
                          <div>
                            <p className="font-medium text-gray-900">{v.vehicle_code}</p>
                            <p className="text-xs text-gray-500">{v.make_model}</p>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <Badge className={v.status === 'AVAILABLE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                            {v.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-right">{v.metrics?.total_trips || 0}</td>
                        <td className="py-3 px-2 text-right">{(v.metrics?.total_distance_km || 0).toLocaleString()}{t("km", "km")}</td>
                        <td className="py-3 px-2 text-right">{v.metrics?.fuel_efficiency || 0}{t("kml", "km/L")}</td>
                        <td className="py-3 px-2 text-right">{t("r", "R$")}{(v.metrics?.total_cost || 0).toLocaleString()}</td>
                        <td className="py-3 px-2 text-right">{t("r", "R$")}{v.metrics?.cost_per_km || 0}</td>
                      </tr>
                    )}
                    {vehiclePerformance.length === 0 &&
                    <tr>
                        <td colSpan={7} className="py-8 text-center text-gray-500">{t("noVehiclePerformance", "No vehicle performance data for this period")}

                      </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Driver Performance Tab */}
        <TabsContent value="drivers">
          <Card>
            <CardHeader>
              <CardTitle>{t("driverPerformanceRankings", "Driver Performance Rankings")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 font-medium text-gray-500">{t("driver", "Driver")}</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-500">{t("status", "Status")}</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">{t("score", "Score")}</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">{t("trips", "Trips")}</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">{t("ontime", "On-Time")}</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">{t("distance", "Distance")}</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">{t("alerts", "Alerts")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {driverPerformance.map((d) =>
                    <tr key={d.driver_id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-medium">
                              {d.name?.split(' ').map((n) => n[0]).join('')}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{d.name}</p>
                              <p className="text-xs text-gray-500">{d.driver_code}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <Badge className={d.status === 'AVAILABLE' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                            {d.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <span className={`font-bold ${
                          d.metrics?.driver_score >= 80 ? 'text-green-600' :
                          d.metrics?.driver_score >= 60 ? 'text-amber-600' : 'text-red-600'}`
                          }>
                              {d.metrics?.driver_score || 0}
                            </span>
                            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                              className={`h-full rounded-full ${
                              d.metrics?.driver_score >= 80 ? 'bg-green-500' :
                              d.metrics?.driver_score >= 60 ? 'bg-amber-500' : 'bg-red-500'}`
                              }
                              style={{ width: `${d.metrics?.driver_score || 0}%` }} />
                            
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-right">{d.metrics?.total_trips || 0}</td>
                        <td className="py-3 px-2 text-right">{d.metrics?.on_time_rate || 0}%</td>
                        <td className="py-3 px-2 text-right">{(d.metrics?.total_distance_km || 0).toLocaleString()}{t("km", "km")}</td>
                        <td className="py-3 px-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {d.metrics?.speeding_events > 0 &&
                          <Badge className="bg-purple-100 text-purple-700 text-xs">
                                {d.metrics.speeding_events}{t("speed", "speed")}
                          </Badge>
                          }
                            {d.metrics?.harsh_braking_events > 0 &&
                          <Badge className="bg-red-100 text-red-700 text-xs">
                                {d.metrics.harsh_braking_events}{t("brake", "brake")}
                          </Badge>
                          }
                            {d.metrics?.total_alerts === 0 &&
                          <span className="text-green-600">{t("clean", "\u2713 Clean")}</span>
                          }
                          </div>
                        </td>
                      </tr>
                    )}
                    {driverPerformance.length === 0 &&
                    <tr>
                        <td colSpan={7} className="py-8 text-center text-gray-500">{t("noDriverPerformance", "No driver performance data for this period")}

                      </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cost Analysis Tab */}
        <TabsContent value="costs">
          {costBreakdown &&
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cost Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-500" />{t("costSummary", "Cost Summary")}

                </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center mb-6">
                    <p className="text-sm text-gray-500">{t("totalCost", "Total Cost (")}{period})</p>
                    <p className="text-4xl font-bold text-gray-900">{t("r", "R$")}
                    {(costBreakdown.costs?.total || 0).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {(costBreakdown.distance_km || 0).toLocaleString()}{t("kmR", "km \u2022 R$")}{costBreakdown.cost_per_km || 0}{t("km", "/km")}
                  </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-600">{t("fuel", "Fuel")}</span>
                        <span className="font-medium">{t("r", "R$")}{(costBreakdown.costs?.fuel || 0).toLocaleString()}</span>
                      </div>
                      <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{ width: `${costBreakdown.breakdown_percentage?.fuel || 0}%` }} />
                      
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{costBreakdown.breakdown_percentage?.fuel || 0}%</p>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-600">{t("labor", "Labor")}</span>
                        <span className="font-medium">{t("r", "R$")}{(costBreakdown.costs?.labor || 0).toLocaleString()}</span>
                      </div>
                      <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div
                        className="h-full bg-amber-500 rounded-full"
                        style={{ width: `${costBreakdown.breakdown_percentage?.labor || 0}%` }} />
                      
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{costBreakdown.breakdown_percentage?.labor || 0}%</p>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-600">{t("parts", "Parts")}</span>
                        <span className="font-medium">{t("r", "R$")}{(costBreakdown.costs?.parts || 0).toLocaleString()}</span>
                      </div>
                      <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div
                        className="h-full bg-purple-500 rounded-full"
                        style={{ width: `${costBreakdown.breakdown_percentage?.parts || 0}%` }} />
                      
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{costBreakdown.breakdown_percentage?.parts || 0}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Cost Insights */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-500" />{t("costInsights", "Cost Insights")}

                </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Fuel className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-blue-900">{t("fuelCost", "Fuel Cost")}</span>
                        </div>
                        <span className="text-lg font-bold text-blue-700">{t("r", "R$")}
                        {(costBreakdown.costs?.fuel || 0).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-blue-600 mt-1">
                        {costBreakdown.breakdown_percentage?.fuel || 0}{t("ofTotalCosts", "% of total costs")}
                    </p>
                    </div>
                    
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Wrench className="w-5 h-5 text-amber-600" />
                          <span className="font-medium text-amber-900">{t("maintenance", "Maintenance")}</span>
                        </div>
                        <span className="text-lg font-bold text-amber-700">{t("r", "R$")}
                        {((costBreakdown.costs?.labor || 0) + (costBreakdown.costs?.parts || 0)).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-amber-600 mt-1">{t("laborPartsCombined", "Labor + Parts combined")}

                    </p>
                    </div>
                    
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Target className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-green-900">{t("costPerKm", "Cost per KM")}</span>
                        </div>
                        <span className="text-lg font-bold text-green-700">{t("r", "R$")}
                        {costBreakdown.cost_per_km || 0}
                        </span>
                      </div>
                      <p className="text-sm text-green-600 mt-1">{t("over", "Over")}
                      {(costBreakdown.distance_km || 0).toLocaleString()}{t("kmTraveled", "km traveled")}
                    </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          }
        </TabsContent>
      </Tabs>
    </div>);

};

export default FleetAnalyticsPage;